import { useCallback, useEffect, useRef, useState } from "react";
import { AsyncSelect, chakraComponents, ControlProps, GroupBase, MultiValueProps } from "chakra-react-select";
import RecipientChipFromOption from "components/Tag/ResourceTargetTag/RecipientChipFromOption";
import {
  Box, Button, Flex, HStack, Icon, Input, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, useToast,
} from "@chakra-ui/react";
import { FiUsers } from "react-icons/fi";
import apiClient from "services/ApiClient";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { EmailAddress, UserOrRoleResponse } from "models/v2/roleUserChannel";
import { CalendarIcon } from "@chakra-ui/icons";
import { startCase } from "lodash";
import { TemplateAttendeeType } from "features/Resources/MeetingTemplate/models";
import { PrefixId } from "features/Resources/Shared/models";
import { UserId } from "features/User/models";
import { TertiaryButton } from "components/Button";
import PlusCirlceButton from "components/Button/PlusCircleButton";
import { userRoleEmailId } from "helpers/string";

export interface UserRoleOrEmailOption {
  recordType: TemplateAttendeeType;
  id: PrefixId | EmailAddress;
  recordId?: UserId
  label: string;
  email?: string;
  name: string;
  emailType?: "personal" | "work";
  /** The user's work email, used to surface the address in the dropdown.
   *  Kept separate from `email` (which is the recipient address for the
   *  CustomEmail variant) so the chip label doesn't accidentally show it. */
  workEmail?: string;
}

// Render selected attendees as the shared CDS.RecipientChip via the
// recordType-aware adapter so styling matches assignee / recipient tags
// elsewhere. Unknown record types fall back to the default chip.
const RECIPIENT_CHIP_TYPES = ["User", "OnboardingJourneyNetworkRelation", "CustomEmail"];

function UserRoleOrEmailMultiValue(props: MultiValueProps<UserRoleOrEmailOption, true, GroupBase<UserRoleOrEmailOption>>) {
  const { data, removeProps } = props;
  if (!RECIPIENT_CHIP_TYPES.includes(data.recordType)) {
    return <chakraComponents.MultiValue {...props} />;
  }
  return (
    <Box mr={1}>
      <RecipientChipFromOption
        recipient={{ recordType: data.recordType, label: data.label, name: data.name, email: data.email }}
        onRemove={removeProps.onClick as (() => void) | undefined}
      />
    </Box>
  );
}

function CustomEmailMenuList(props: any) {
  const { selectProps, children } = props;
  const { setShowEmailInput } = selectProps.customProps;

  return (
    <chakraComponents.MenuList {...props}>
      <Box p={2}>
        <Button
          size="sm"
          width="100%"
          variant="ghost"
          fontWeight="semibold"
          color="brand.600"
          bg="brand.50"
          _hover={{ bg: "brand.100" }}
          _active={{ bg: "brand.100" }}
          onClick={() => setShowEmailInput(true)}
        >
          + Add Email Address
        </Button>
      </Box>
      {children}
    </chakraComponents.MenuList>
  );
}

interface CustomSelectProps {
  label: string;
  setShouldOpenMenu: (val: boolean) => void;
  isSearchable: boolean;
  menuIsOpen: boolean;
}

function CustomControl(
  props: ControlProps<UserRoleOrEmailOption, true> & {
    selectProps: { customProps: CustomSelectProps };
  },
) {
  const {
    children,
    selectProps: { customProps: { menuIsOpen, isSearchable, setShouldOpenMenu, label } },
    innerRef,
    innerProps,
  } = props;

  // If open and searchable, render the default control (with input)
  if (menuIsOpen && isSearchable) {
    return <chakraComponents.Control {...props} />;
  }

  return (
    <chakraComponents.Control {...props}>
      <Flex
        ref={innerRef}
        {...innerProps}
        w="100%"
        align="start"
        justify="flex-start"
        gap={2}
        flexWrap="wrap"
        pl={2}
        pr={2}
      >
        {children}

        <PlusCirlceButton
          mt="2px"
          label={label}
          onClick={(e) => {
            e.stopPropagation();
            setShouldOpenMenu(true);
          }}
        />
      </Flex>
    </chakraComponents.Control>
  );
}

interface UserRoleOrEmailMultiSelectProps {
  value: UserRoleOrEmailOption[];
  onChange: (value: UserRoleOrEmailOption[]) => void;
  includeRole?: boolean;
  label?: string;
  includePersonal?: boolean;
}

export default function UserRoleOrEmailMultiSelect({
  value,
  onChange,
  includeRole = true,
  label = "Add User, Role or Email",
  includePersonal = false,
}: UserRoleOrEmailMultiSelectProps) {
  const toast = useToast();
  const types = includeRole ? ["users", "roles"] : ["users"];
  const query = types.map((t) => `types[]=${encodeURIComponent(t)}`).join("&");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showEmailInput && inputRef.current) {
      inputRef.current?.focus();
    }
    if (!showEmailInput && inputRef.current) {
      inputRef.current.value = ""; // Clear input when popover closes
    }
  }, [showEmailInput]);

  const loadOptions = useCallback(
    async (inputValue: string): Promise<UserRoleOrEmailOption[]> => {
      try {
        // apiClient handles auth + case conversion (raw fetch was returning a
        // logged-out HTML response, breaking the search silently). It also
        // unwraps the `{data: [...]}` envelope into a plain array.
        // `no-store` forces a fresh fetch every keystroke — a search endpoint
        // should never serve a 304 with a stale cached body.
        const results = await apiClient.get<UserOrRoleResponse[]>(
          `${API_ROUTES.rolesUsersChannels}?${query}&q=${encodeURIComponent(inputValue)}`,
          undefined,
          "no-store",
        );

        const options: UserRoleOrEmailOption[] = (results || []).flatMap(
          (userOrRole: UserOrRoleResponse) => {
            if (includePersonal && userOrRole.recordType === "OnboardingJourneyNetworkRelation" && userOrRole.name === "new_hire") {
              return [
                {
                  recordType: userOrRole.recordType,
                  recordId: userOrRole.id,
                  id: userRoleEmailId(undefined, userOrRole.recordType, userOrRole.id, "work" as const),
                  label: `${startCase(userOrRole.name)} (work)`,
                  name: userOrRole.name,
                  emailType: "work" as const,
                },
                {
                  recordType: userOrRole.recordType,
                  recordId: userOrRole.id,
                  id: userRoleEmailId(undefined, userOrRole.recordType, userOrRole.id, "personal" as const),
                  label: `${startCase(userOrRole.name)} (personal)`,
                  name: userOrRole.name,
                  emailType: "personal" as const,
                },
              ];
            }

            return [
              {
                recordType: userOrRole.recordType,
                recordId: userOrRole.id,
                id: userRoleEmailId(undefined, userOrRole.recordType, userOrRole.id, "work" as const),
                label: startCase(userOrRole.name),
                name: userOrRole.name,
                emailType: "work" as const,
                workEmail: (userOrRole as any).workEmail,
              },
            ];
          },
        );

        // Exclude already selected
        const selectedIds = new Set(
          value.map((v) => v.id).filter((id): id is string => !!id),
        );

        const filteredOptions = options.filter(
          (opt) => !selectedIds.has(opt.id),
        );

        return filteredOptions;
      } catch (error) {
        console.error("Failed to load options", error);
        return [];
      }
    },
    [includePersonal, query, value],
  );

  function isValidEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Remove duplicates (e.g., user and email with same address)
  function dedupeRecipients(values: UserRoleOrEmailOption[]): UserRoleOrEmailOption[] {
    const emails = new Set<string>();
    return values.filter((option) => {
      if (option.email) {
        const e = option.email.toLowerCase();
        if (emails.has(e)) return false;
        emails.add(e);
      }
      return true;
    });
  }

  const handleCreate = useCallback((inputValue: string) => {
    const normalized = inputValue.trim().toLowerCase();

    // Check if any existing loaded user matches the email
    const match = value.find(
      (o) => o.recordType === "User" && o.email?.toLowerCase() === normalized,
    );

    if (match) {
      onChange(dedupeRecipients([...(value || []), match]));
      setShowEmailInput(false);
      return true;
    }

    if (!isValidEmail(normalized)) {
      toast({
        title: "Error",
        description: `Invalid email ${normalized} - Please enter a valid email.`,
        status: "error",
      });
      return false;
    }

    onChange(dedupeRecipients([...(value || []), {
      recordType: "CustomEmail",
      label: inputValue,
      name: inputValue,
      id: inputValue,
      email: inputValue,
    }]));
    setShowEmailInput(false);
    return true;
  }, [onChange, toast, value]);

  const onShowEmailInput = useCallback(() => {
    setMenuIsOpen(false);
    setShowEmailInput(true);
  }, []);

  const formatOptionLabel = useCallback(
    (option: UserRoleOrEmailOption, meta?: { context?: "menu" | "value" }) => {
      // Show the work email only when rendering inside the dropdown menu —
      // the selected chip stays clean (name only).
      const showEmail = meta?.context === "menu";
      if (option.recordType === "User") {
        return (
          // TODO: This should utilize role if available
          <Flex align="center" gap="2">
            {option.label}
            {showEmail && option.workEmail && (
              <Box as="span" color="fg.subtle">— {option.workEmail}</Box>
            )}
          </Flex>
        );
      }
      if (option.recordType === "OnboardingJourneyNetworkRelation") {
        return (
          <Flex align="center" gap="2">
            <Icon as={FiUsers} />
            {option.label}
          </Flex>
        );
      }
      if (option.recordType === "CustomEmail") {
        return (
          <Flex align="center" gap="2">
            <Icon as={CalendarIcon} />
            {option.label}
          </Flex>
        );
      }
      return option.label;
    },
    [],
  );

  return (
    <Popover
      isOpen={showEmailInput}
      onClose={() => setShowEmailInput(false)}
      placement="bottom-start"
      initialFocusRef={inputRef}
    >
      <PopoverTrigger>
        <Box>
          <AsyncSelect<UserRoleOrEmailOption, true, GroupBase<UserRoleOrEmailOption>>
            key={value.map((v) => v.id).join(",")}
            isMulti
            cacheOptions
            defaultOptions
            menuIsOpen={menuIsOpen}
            openMenuOnClick={false}
            onMenuOpen={() => {
              setMenuIsOpen(true);
              setShowEmailInput(false);
            }}
            onMenuClose={() => setMenuIsOpen(false)}
            isClearable={false}
            loadOptions={loadOptions}
            value={value}
            onChange={(newValues) => {
              onChange(dedupeRecipients(Array.from(newValues || [])));
            }}
            placeholder="Select users, roles, or enter emails..."
            formatOptionLabel={formatOptionLabel}
            getOptionValue={(o) => `${o.recordType}_${o.id || o.email || o.label}`}
            variant="unstyled"
            components={{
              DropdownIndicator: null,
              MenuList: CustomEmailMenuList,
              Control: CustomControl,
              MultiValue: UserRoleOrEmailMultiValue,
            }}
            customProps={{
              menuIsOpen,
              setShouldOpenMenu: (shouldOpen: boolean) => setMenuIsOpen(shouldOpen),
              isSearchable: true,
              label,
              setShowEmailInput: onShowEmailInput,
            }}
            chakraStyles={{
              container: (base) => ({
                ...base,
                width: "100%",
              }),
              menu: (provided) => ({
                ...provided,
                width: "400px",
                maxWidth: "400px",
                zIndex: 3,
              }),
            }}
          />
        </Box>
      </PopoverTrigger>
      <PopoverContent
        width="600px"
        boxShadow="lg"
        p={3}
        zIndex={10}
        ref={selectRef}
      >
        <PopoverArrow />
        <PopoverBody>
          <HStack spacing={2}>
            <Input
              ref={inputRef}
              size="sm"
              placeholder="Enter email"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (inputRef.current?.value) handleCreate(inputRef.current.value);
                }
              }}
            />
            <TertiaryButton
              size="sm"
              onClick={() => {
                if (inputRef.current?.value) handleCreate(inputRef.current.value);
              }}
            >
              Add
            </TertiaryButton>
          </HStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
